import { CalendarCheck, CalendarClock, CalendarOff } from "lucide-react";
import { Link } from "react-router-dom";
import type { CalendarSource, PlannedBasis } from "@opus-dt/shared-types";
import { Pill, type PillTone } from "@/components/ui/pill";
import { useAuthStore } from "@/stores/auth.store";
import { cn } from "@/lib/utils";

// De ONDE saiu o denominador da Disponibilidade.
// O tempo planejado passou a ter três origens. Sem dizer qual está em uso, uma linha sem expediente
// cadastrado exibe um indicador perfeitamente plausível e o leitor conclui, sozinho e errado, que
// ele foi medido contra o expediente. O badge não muda nenhum valor — ele impede uma leitura falsa.
// A ausência de expediente é o caso que MAIS precisa aparecer, então ela recebe tom de aviso e o
// caminho para resolver; o caso normal fica em tom neutro para não virar ruído em toda tela.

interface Props {
  basis: PlannedBasis;
  /**
   * DE QUEM é o expediente. Opcional porque a resposta atravessa a rede: um payload em
   * cache de antes do deploy chega sem o campo, e o badge precisa continuar renderizando os três
   * casos estabelecidos.
   */
  calendarSource?: CalendarSource;
  lineId: string;
  className?: string;
}

const COPY: Record<
  PlannedBasis,
  { tone: PillTone; label: string; hint: string; Icon: typeof CalendarCheck }
> = {
  calendar: {
    tone: "neutral",
    label: "Expediente da linha",
    hint: "base do tempo planejado",
    Icon: CalendarCheck,
  },
  run: {
    tone: "warning",
    label: "Janela da ordem",
    // Verdadeiro nos DOIS caminhos que levam a `run`: linha sem calendário e calendário que declara
    // a janela fechada. Dizer "a linha não tem expediente" seria falso no segundo.
    hint: "o expediente da linha não cobre esta janela",
    Icon: CalendarClock,
  },
  none: {
    tone: "warning",
    label: "Sem base",
    hint: "sem expediente e sem ordem planejada — Disponibilidade e Desempenho indisponíveis",
    Icon: CalendarOff,
  },
};

export function PlannedBasisBadge({ basis, calendarSource, lineId, className }: Props) {
  // A leitura do expediente é aberta, mas cadastrar exige GESTOR. Oferecer o atalho a quem
  // não pode agir é convidar para uma porta fechada.
  const isGestor = useAuthStore((s) => s.user?.role === "GESTOR");

  // A base atravessa a rede, e tipo não sobrevive a essa fronteira: uma resposta em cache de antes
  // do deploy, ou um payload de um serviço mais antigo, chega sem o campo. Um badge INFORMATIVO que
  // estoura leva o card inteiro junto (o erro de render sobe para o SentryErrorBoundary) — a leitura
  // do indicador some por causa do rótulo que só deveria explicá-la. Sem base conhecida, não há o
  // que dizer: não renderiza e o número continua na tela.
  const copy = COPY[basis];
  if (!copy) return null;

  const { tone, hint, Icon } = copy;
  const showShortcut = isGestor && basis !== "calendar";

  // A ORIGEM entra no rótulo, não num badge a mais.
  // Duas linhas lado a lado ("Expediente da linha" + "vindo da fábrica") custariam uma pílula extra
  // em toda tela de indicador para dizer uma coisa só. E o texto responde a pergunta que o gestor
  // de fato faz ao ver o número mudar: "esse expediente é meu ou da fábrica?".
  // Os TRÊS CASOS ANTERIORES seguem idênticos: sem `calendarSource`, o rótulo é o de sempre. É o
  // que permite uma resposta em cache antiga renderizar como renderizava.
  const label =
    basis === "calendar" && calendarSource === "organization"
      ? "Expediente da fábrica"
      : copy.label;

  return (
    <div
      className={cn("flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]", className)}
      data-testid="planned-basis"
      data-basis={basis}
      data-source={calendarSource ?? undefined}
    >
      <Pill tone={tone} icon={<Icon className="h-3 w-3 shrink-0" aria-hidden />}>
        <span className="sr-only">Base do tempo planejado: </span>
        {label}
      </Pill>
      <span className="text-text-tertiary">
        {basis === "calendar" && calendarSource === "organization"
          ? "esta linha segue o padrão da fábrica"
          : hint}
      </span>
      {showShortcut ? (
        <Link
          to={`/lines/${lineId}/edit`}
          className="text-text-primary underline decoration-dotted underline-offset-2 hover:decoration-solid focus-visible:outline-none focus-visible:[box-shadow:var(--rd-shadow-focus)]"
        >
          Definir expediente
        </Link>
      ) : null}
    </div>
  );
}
